import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Clock, TrendingUp, BarChart3, ChevronLeft, ChevronRight, 
  Play, Pause, RefreshCw, Search, MapPin, Sparkles, ArrowRight,
  Target, Calendar
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend, AreaChart, Area } from 'recharts';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';

const API = process.env.REACT_APP_BACKEND_URL;

// Party colors
const PARTY_COLORS = {
  conservative: '#0087DC',
  labour: '#E4003B',
  libdem: '#FAA61A',
  reform: '#12B6CF',
  green: '#6AB023',
  snp: '#FDF38E',
  plaid: '#005B54',
  other: '#808080'
};

const PARTY_LABELS = {
  conservative: 'Conservative',
  labour: 'Labour',
  libdem: 'Liberal/Lib Dem',
  reform: 'Reform UK',
  green: 'Green',
  snp: 'SNP',
  plaid: 'Plaid Cymru',
  other: 'Other'
};

// 2024 baseline for projections
const BASELINE_2024 = {
  conservative: { seats: 121, vote: 24.4 },
  labour: { seats: 412, vote: 34.7 },
  libdem: { seats: 72, vote: 12.6 },
  reform: { seats: 5, vote: 14.7 },
  green: { seats: 4, vote: 6.9 },
  snp: { seats: 9, vote: 2.6 },
  plaid: { seats: 4, vote: 0.7 },
  other: { seats: 23, vote: 3.4 }
};

// Default current polling (Feb 2026)
const DEFAULT_POLLING = {
  reform: 29,
  labour: 19,
  conservative: 18,
  green: 15,
  libdem: 12,
  snp: 3,
  plaid: 1,
  other: 3
};

const HistoricalElectionsPage = () => {
  const [elections, setElections] = useState({});
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [constituencySearch, setConstituencySearch] = useState('');
  const [constituencyHistory, setConstituencyHistory] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [viewMode, setViewMode] = useState('timeline'); // timeline, projection
  const [projectionPolling, setProjectionPolling] = useState(DEFAULT_POLLING);
  const [projectionYear, setProjectionYear] = useState(2029);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-play through years
  useEffect(() => {
    if (!isPlaying || years.length === 0) return;
    
    const interval = setInterval(() => {
      setSelectedYear(prev => {
        const currentIndex = years.indexOf(prev);
        if (currentIndex >= years.length - 1) {
          setIsPlaying(false);
          return prev;
        }
        return years[currentIndex + 1];
      });
    }, 1500);
    
    return () => clearInterval(interval);
  }, [isPlaying, years]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/election-results/historical`);
      if (response.data?.elections) {
        setElections(response.data.elections);
        const sortedYears = response.data.years.map(y => parseInt(y)).sort((a, b) => a - b);
        setYears(sortedYears);
        setSelectedYear(sortedYears[sortedYears.length - 1]); // Start at 2024
      }
    } catch (error) {
      console.error('Error fetching historical data:', error);
    }
    setLoading(false);
  };

  // Search for constituency history
  const searchConstituency = async () => {
    if (!constituencySearch.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/api/election-results/constituency/${encodeURIComponent(constituencySearch)}`);
      setConstituencyHistory(response.data);
    } catch (error) {
      console.error('Error searching constituency:', error);
      setConstituencyHistory({ query: constituencySearch, history: [], error: 'Search failed' });
    }
    setSearchLoading(false);
  };

  // Calculate projected seats using UNS
  const calculateProjection = (polling) => {
    const projected = {};
    let totalSeats = 0;
    
    Object.keys(BASELINE_2024).forEach(party => {
      const baseline = BASELINE_2024[party];
      const currentPoll = polling[party] || 0;
      const swing = currentPoll - baseline.vote;
      
      // Simple UNS model: seats change proportionally to swing
      const swingFactor = 1 + (swing / 100) * 3; // 1% swing = ~3% seat change
      let newSeats = Math.round(baseline.seats * swingFactor);
      newSeats = Math.max(0, newSeats);
      projected[party] = newSeats;
      totalSeats += newSeats;
    });
    
    // Normalize to 650 seats
    if (totalSeats !== 650) {
      const scale = 650 / totalSeats;
      let adjusted = 0;
      Object.keys(projected).forEach(party => {
        projected[party] = Math.round(projected[party] * scale);
        adjusted += projected[party];
      });
      // Fix rounding errors
      projected.other += (650 - adjusted);
    }
    
    return projected;
  };

  // Generate future projection years
  const futureProjections = useMemo(() => {
    const projectionYears = [2029, 2034];
    const projected = calculateProjection(projectionPolling);
    
    return projectionYears.map(year => ({
      year,
      ...projected,
      total: 650
    }));
  }, [projectionPolling]);

  // Prepare chart data with 2024 and projections
  const chartData = useMemo(() => {
    const historical = years.map(year => {
      const data = elections[year] || {};
      return {
        year,
        conservative: data.conservative || 0,
        labour: data.labour || 0,
        libdem: data.libdem || 0,
        reform: data.reform || 0,
        snp: data.snp || 0,
        green: data.green || 0,
        other: data.other || 0,
        total: data.total || 650,
        isProjection: false
      };
    });
    
    // Add projections if in projection mode
    if (viewMode === 'projection') {
      return [...historical, ...futureProjections.map(p => ({...p, isProjection: true}))];
    }
    
    return historical;
  }, [elections, years, viewMode, futureProjections]);

  // Get data for selected year
  const selectedData = useMemo(() => {
    if (!selectedYear || !elections[selectedYear]) return null;
    return elections[selectedYear];
  }, [selectedYear, elections]);

  // Navigate between years
  const goToPreviousYear = () => {
    const currentIndex = years.indexOf(selectedYear);
    if (currentIndex > 0) {
      setSelectedYear(years[currentIndex - 1]);
    }
  };

  const goToNextYear = () => {
    const currentIndex = years.indexOf(selectedYear);
    if (currentIndex < years.length - 1) {
      setSelectedYear(years[currentIndex + 1]);
    }
  };

  // Determine government type for a year
  const getGovernmentType = (data) => {
    if (!data) return 'Unknown';
    const majority = Math.floor((data.total || 650) / 2) + 1;
    if (data.conservative >= majority) return 'Conservative Majority';
    if (data.labour >= majority) return 'Labour Majority';
    if (data.conservative > data.labour) return 'Conservative (Minority/Coalition)';
    if (data.labour > data.conservative) return 'Labour (Minority/Coalition)';
    return 'Hung Parliament';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-black mx-auto mb-4" />
          <p className="text-slate-600 font-mono text-sm">Loading historical elections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20" data-testid="historical-elections-page">
      <SEO 
        title="UK Election History 1955-2024 & Future Projections"
        description="Explore 70 years of UK General Election results and see projections through 2034. Interactive timeline from 1955 to 2024."
        keywords="UK election history, general election results, Conservative Labour seats, electoral history, British politics, election projections"
      />

      {/* Hero Section */}
      <section className="border-b border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="h-6 w-6" />
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500">Electoral Archive</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight mb-4">
            UK Election History
          </h1>
          <p className="text-slate-600 max-w-2xl text-lg">
            70 years of UK General Elections from 1955 to 2024, plus projections through 2034.
          </p>
          
          {/* View Mode Toggle */}
          <div className="mt-6 flex gap-2">
            <Button
              variant={viewMode === 'timeline' ? 'default' : 'outline'}
              onClick={() => setViewMode('timeline')}
              className="rounded-none"
            >
              <Clock className="h-4 w-4 mr-2" />
              Historical Timeline
            </Button>
            <Button
              variant={viewMode === 'projection' ? 'default' : 'outline'}
              onClick={() => setViewMode('projection')}
              className="rounded-none"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Future Projections
            </Button>
          </div>
          
          {/* Year Selector */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <Button 
              variant="outline" 
              size="icon"
              onClick={goToPreviousYear}
              disabled={years.indexOf(selectedYear) === 0}
              className="rounded-none border-black"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            
            <div className="text-center">
              <div className="font-mono text-6xl md:text-8xl font-light">
                {selectedYear}
              </div>
              <div className="text-slate-500 text-sm mt-1 font-mono">
                {getGovernmentType(selectedData)}
              </div>
            </div>
            
            <Button 
              variant="outline" 
              size="icon"
              onClick={goToNextYear}
              disabled={years.indexOf(selectedYear) === years.length - 1}
              className="rounded-none border-black"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Play/Pause Controls */}
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              onClick={() => setIsPlaying(!isPlaying)}
              className="rounded-none border-black"
            >
              {isPlaying ? (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Play Timeline
                </>
              )}
            </Button>
          </div>
          
          {/* Year Pills */}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {years.map(year => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-3 py-1 text-sm font-mono transition-colors ${
                  year === selectedYear 
                    ? 'bg-black text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Future Projections Panel */}
      {viewMode === 'projection' && (
        <section className="border-b border-slate-200 py-8 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="h-5 w-5" />
              <h2 className="font-serif text-2xl">2029-2034 Projection Model</h2>
            </div>
            
            <p className="text-slate-600 mb-6">
              Adjust polling assumptions to see how future elections might unfold. Based on Uniform National Swing from 2024 baseline.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {['reform', 'labour', 'conservative', 'libdem', 'green'].map(party => (
                <div key={party} className="space-y-2 bg-white p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3" 
                        style={{ backgroundColor: PARTY_COLORS[party] }}
                      />
                      <span className="text-sm font-medium">{PARTY_LABELS[party]}</span>
                    </div>
                    <span className="font-mono font-bold">{projectionPolling[party]}%</span>
                  </div>
                  <Slider
                    value={[projectionPolling[party]]}
                    onValueChange={([value]) => setProjectionPolling(prev => ({...prev, [party]: value}))}
                    min={0}
                    max={50}
                    step={1}
                  />
                  <div className="text-xs text-slate-500 font-mono">
                    2024: {BASELINE_2024[party].vote}%
                  </div>
                </div>
              ))}
            </div>
            
            {/* Projected Results */}
            <div className="grid md:grid-cols-2 gap-4">
              {futureProjections.map(proj => (
                <div key={proj.year} className="bg-white border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono text-2xl">{proj.year}</div>
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 font-medium">
                      PROJECTION
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(proj)
                      .filter(([key]) => !['year', 'total', 'isProjection'].includes(key) && proj[key] > 0)
                      .sort(([,a], [,b]) => b - a)
                      .map(([party, seats]) => (
                        <div key={party} className="flex items-center gap-1.5">
                          <div 
                            className="w-2 h-2"
                            style={{ backgroundColor: PARTY_COLORS[party] }}
                          />
                          <span className="text-xs">{PARTY_LABELS[party]?.split('/')[0]}</span>
                          <span className="font-mono text-xs font-bold">{seats}</span>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
            
            <Button
              variant="outline"
              onClick={() => setProjectionPolling(DEFAULT_POLLING)}
              className="mt-4 rounded-none border-black"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset to Current Polling
            </Button>
          </div>
        </section>
      )}

      {/* Selected Year Results */}
      {selectedData && (
        <section className="max-w-7xl mx-auto px-4 py-8">
          <h2 className="font-serif text-2xl mb-4">{selectedYear} Election Results</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {Object.entries(selectedData)
              .filter(([key, value]) => key !== 'total' && value > 0)
              .sort(([,a], [,b]) => b - a)
              .map(([party, seats]) => {
                const pct = ((seats / selectedData.total) * 100).toFixed(1);
                return (
                  <div 
                    key={party}
                    className="bg-white border border-slate-200 p-4 text-center"
                    style={{ borderTopColor: PARTY_COLORS[party], borderTopWidth: '3px' }}
                  >
                    <div className="text-xs text-slate-500 uppercase tracking-wide font-mono">
                      {PARTY_LABELS[party]?.split('/')[0] || party}
                    </div>
                    <div className="text-2xl font-mono font-bold mt-1" style={{ color: PARTY_COLORS[party] }}>
                      {seats}
                    </div>
                    <div className="text-xs text-slate-400 font-mono">{pct}%</div>
                  </div>
                );
              })}
          </div>
        </section>
      )}

      {/* Historical Timeline Chart */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="font-serif text-2xl mb-4">
          {viewMode === 'projection' ? '70 Years + Future Projections' : '70 Years of UK Elections'}
        </h2>
        
        {/* Stacked Area Chart */}
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="font-medium mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Seats Over Time
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData}>
              <XAxis 
                dataKey="year" 
                tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                domain={[0, 700]} 
                tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip 
                formatter={(value, name) => [value, PARTY_LABELS[name]]}
                labelFormatter={(year) => {
                  const data = chartData.find(d => d.year === year);
                  return data?.isProjection ? `${year} (Projection)` : `${year} Election`;
                }}
                contentStyle={{ borderRadius: 0, border: '1px solid #e2e8f0' }}
              />
              <Legend 
                formatter={(value) => PARTY_LABELS[value]} 
                wrapperStyle={{ fontFamily: 'Inter', fontSize: 12 }}
              />
              <Area type="monotone" dataKey="conservative" stackId="1" fill={PARTY_COLORS.conservative} stroke={PARTY_COLORS.conservative} />
              <Area type="monotone" dataKey="labour" stackId="1" fill={PARTY_COLORS.labour} stroke={PARTY_COLORS.labour} />
              <Area type="monotone" dataKey="libdem" stackId="1" fill={PARTY_COLORS.libdem} stroke={PARTY_COLORS.libdem} />
              {viewMode === 'projection' && (
                <Area type="monotone" dataKey="reform" stackId="1" fill={PARTY_COLORS.reform} stroke={PARTY_COLORS.reform} />
              )}
              <Area type="monotone" dataKey="snp" stackId="1" fill={PARTY_COLORS.snp} stroke={PARTY_COLORS.snp} />
              <Area type="monotone" dataKey="other" stackId="1" fill={PARTY_COLORS.other} stroke={PARTY_COLORS.other} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Line Chart - Two Party Race */}
        <div className="bg-white border border-slate-200 p-6 mt-6">
          <h3 className="font-medium mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Conservative vs Labour
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <XAxis 
                dataKey="year" 
                tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                domain={[0, 500]} 
                tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip 
                formatter={(value, name) => [value, PARTY_LABELS[name]]}
                contentStyle={{ borderRadius: 0, border: '1px solid #e2e8f0' }}
              />
              <Legend formatter={(value) => PARTY_LABELS[value]} />
              <Line 
                type="monotone" 
                dataKey="conservative" 
                stroke={PARTY_COLORS.conservative} 
                strokeWidth={3}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="labour" 
                stroke={PARTY_COLORS.labour} 
                strokeWidth={3}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              {viewMode === 'projection' && (
                <Line 
                  type="monotone" 
                  dataKey="reform" 
                  stroke={PARTY_COLORS.reform} 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 3 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Constituency History Search */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white border border-slate-200 p-6">
          <h2 className="font-serif text-2xl mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Constituency History
          </h2>
          <p className="text-slate-600 mb-4">
            Search for any constituency to see how it voted in every election from 1955 to 2024.
          </p>
          
          {/* Search Input */}
          <div className="flex gap-2 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="e.g. Manchester, Bristol, Edinburgh..."
                value={constituencySearch}
                onChange={(e) => setConstituencySearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchConstituency()}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 focus:border-black focus:outline-none font-mono"
                data-testid="constituency-search"
              />
            </div>
            <Button 
              onClick={searchConstituency}
              disabled={searchLoading || !constituencySearch.trim()}
              className="px-6 rounded-none bg-black text-white hover:bg-slate-800"
            >
              {searchLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </div>
          
          {/* Results */}
          {constituencyHistory && (
            <div className="mt-6">
              {constituencyHistory.history?.length > 0 ? (
                <>
                  <h3 className="font-mono text-lg mb-4">
                    {constituencyHistory.history[0]?.name} — {constituencyHistory.elections_found} Elections
                  </h3>
                  
                  {/* Timeline View */}
                  <div className="overflow-x-auto">
                    <div className="flex gap-1 pb-4 min-w-max">
                      {constituencyHistory.history.map(election => (
                        <div 
                          key={election.year}
                          className="flex-shrink-0 w-16 text-center p-2"
                          style={{ backgroundColor: PARTY_COLORS[election.winner] || '#808080' }}
                        >
                          <div className="text-white text-xs font-mono font-bold">{election.year}</div>
                          <div className="text-white/80 text-[10px] uppercase">
                            {PARTY_LABELS[election.winner]?.split('/')[0]?.substring(0, 3) || election.winner?.substring(0, 3)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Party Count Summary */}
                  <div className="mt-4 flex flex-wrap gap-3">
                    {Object.entries(
                      constituencyHistory.history.reduce((acc, e) => {
                        acc[e.winner] = (acc[e.winner] || 0) + 1;
                        return acc;
                      }, {})
                    ).sort(([,a], [,b]) => b - a).map(([party, count]) => (
                      <div 
                        key={party}
                        className="flex items-center gap-2 px-3 py-1.5 text-white text-sm font-mono"
                        style={{ backgroundColor: PARTY_COLORS[party] || '#808080' }}
                      >
                        <span>{PARTY_LABELS[party]?.split('/')[0] || party}</span>
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-slate-500 font-mono">
                  No results found for "{constituencyHistory.query}". 
                  Try a different spelling or a nearby constituency.
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Key Historical Events */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="font-serif text-2xl mb-4">Key Electoral Moments</h2>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 p-4">
            <div className="font-mono text-3xl font-bold" style={{ color: PARTY_COLORS.conservative }}>1983</div>
            <div className="font-medium">Thatcher's Landslide</div>
            <p className="text-sm text-slate-500 mt-1">
              412 seats — largest Conservative majority since 1935.
            </p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="font-mono text-3xl font-bold" style={{ color: PARTY_COLORS.labour }}>1997</div>
            <div className="font-medium">Blair's New Labour</div>
            <p className="text-sm text-slate-500 mt-1">
              418 seats — Labour's largest ever majority.
            </p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="font-mono text-3xl font-bold text-amber-600">2010</div>
            <div className="font-medium">Coalition Era</div>
            <p className="text-sm text-slate-500 mt-1">
              First hung parliament since 1974. Lib Dems peaked at 57 seats.
            </p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="font-mono text-3xl font-bold" style={{ color: PARTY_COLORS.labour }}>2024</div>
            <div className="font-medium">Labour's Return</div>
            <p className="text-sm text-slate-500 mt-1">
              412 seats — largest swing since 1945. Reform enters Parliament.
            </p>
          </div>
        </div>
      </section>

      {/* Methodology Note */}
      <section className="max-w-7xl mx-auto px-4 pb-10">
        <div className="bg-slate-50 border border-slate-200 p-6">
          <h3 className="font-medium mb-2">Data Sources & Methodology</h3>
          <p className="text-sm text-slate-600">
            Historical election data (1955-2019) from Electoral Calculus and Richard Kimber's Political Science Resources. 
            2024 results from UK Parliament official election data. Projections use Uniform National Swing (UNS) model — 
            a simplified approach that assumes polling changes apply uniformly across all constituencies. 
            Actual results may vary significantly due to local factors, tactical voting, and boundary changes.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HistoricalElectionsPage;
